<template>
  <StandardEmptyPageContainer>
    <LoadingCircle v-if="loading" />
    <div v-else>
      <v-row no-gutters>
        <v-col cols="12">
          <HeaderWidget
            :header-text="
              riskCategoryToDescriptionText(statsStore.selectedRiskCategory)
            "
            :type="statsStore.selectedRiskCategory"
            :icon="setIcon(statsStore.selectedRiskCategory)"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <div v-if="!hasRiskStats" class="text-center">
            <p>None of your suppliers have been flagged</p>
          </div>
          <DetailedReasonOverview
            v-else
            :data="riskStats"
            @go-to-unit-profile="routeToUnitProfile"
          />
        </v-col>
      </v-row>
    </div>
  </StandardEmptyPageContainer>
</template>

<script setup lang="ts">
import {
  RiskCategory,
  riskCategoryToDescriptionText,
} from "@/models/enum/riskCategory";
import { computed, ref, onBeforeMount } from "vue";
import { getRiskStats } from "@/services/statsService";
import type { RiskStatsDto } from "@/models/dto/riskStatsDto";
import { useQuestionnairesStore } from "@/stores/questionnaires";
import HeaderWidget from "@/components/widgets/HeaderWidget.vue";
import { RouterPath } from "@/models/enum/routerPath";
import { useStatsStore } from "@/stores/statsStore";
import LoadingCircle from "@/components/custom/LoadingCircle.vue";
import { genericError } from "@/modules/utilities";
import router from "@/router";
import { useUnitProfileStore } from "@/stores/unitProfileStore";
import { useSnackBarStore } from "@/stores/snackBarStore";
import DetailedReasonOverview from "@/views/dashboard-view/DetailedReasonOverview.vue";
import StandardEmptyPageContainer from "@/components/containers/StandardEmptyPageContainer.vue";

const questionnaireStore = useQuestionnairesStore();
const statsStore = useStatsStore();
const unitProfileStore = useUnitProfileStore();
const snackbarStore = useSnackBarStore();

const loading = ref(false);
const riskStats = ref(new Array<RiskStatsDto>());
const hasRiskStats = computed(() => riskStats.value.length > 0);

onBeforeMount(async () => {
  // TODO Add all selected questionnaires in store when we have multiselect implemented
  loading.value = true;
  try {
    riskStats.value = await getRiskStats(
      questionnaireStore.selectedQuestionnaire.id,
      statsStore.selectedRiskCategory
    );
  } catch (error) {
    snackbarStore.showError(genericError);
  } finally {
    loading.value = false;
  }
});

async function routeToUnitProfile(risk: RiskStatsDto) {
  try {
    loading.value = true;
    await unitProfileStore.getUnitProfileWithTrustGroupId(
      questionnaireStore.selectedQuestionnaire.id,
      risk.trustGroupId
    );
    router.push(RouterPath.UnitProfile);
  } catch (error: any) {
    snackbarStore.showError(error.message);
  } finally {
    loading.value = false;
  }
}

function setIcon(title: RiskCategory): string {
  switch (title) {
    case RiskCategory.LabourAndHumanRight:
      return "icon-family-outline";
    case RiskCategory.HealthAndSafety:
      return "icon-governance-outline";
    case RiskCategory.Environment:
      return "icon-growth-outline";
    case RiskCategory.BusinessIntegrity:
      return "icon-venn-abc-outline";
    case RiskCategory.SupplierManagement:
      return "icon-manufacturer-outline";
    default:
      return "icon-circle-more-outline";
  }
}
</script>
